import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiRotateCcw, FiSearch, FiShare2, FiCopy, FiEye, FiSave } from 'react-icons/fi';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import { DEFAULT_MAP } from '../../utils/waitMetricMap';
import { ANY_KEY, itemsForKey, withItems, withoutKey, keysOf, diffOf, changedCount } from '../../utils/perfSettingsMap';
import './PerfMetricMapModal.css';   // 매핑 편집기 모양(pmm-*) — 예전 「매핑 편집」 창의 것을 그대로 쓴다
import './PerfSettingsPanel.css';

/**
 * 오라클 성능 분석 › 설정 탭(2026-10-16 사용자 요청) — 대기이벤트와 함께 볼 것의 **매핑 두 가지**를 정한다.
 *   - 시스템 메트릭: 실시간 성능·성능 이력에서 대기를 고르면 아래에 서는 지표(예전의 「매핑 편집」 창 — 창 대신 이 탭으로 옮겼다)
 *   - 설정 파라미터: 「관련 설정」 탭에 보이는 파라미터·DB 설정·REDO 현황
 * 사용자 것은 **고친 키만** 저장된다 — 기본값과 다른 키에 「변경됨」, 항목마다 「추가」·「뺌」 을 붙인다.
 * 공개 — 내 설정을 공개하면 다른 사용자가 보고 「내 설정으로 복제」(사본) 할 수 있다. 주인이 나중에 고쳐도 복제한 쪽은 그대로다.
 */
const keyLabel = (key, t) => {
    if (key === ANY_KEY) return t('monitoring:perf.map.keyAny');
    if (key.startsWith('class:')) return t('monitoring:perf.map.keyClass', { name: key.slice(6) });
    return key;
};

/** 한 매핑의 편집기 — 왼쪽 대기 목록, 오른쪽 그 대기에 붙일 항목(체크) */
function MapEditor({ kind, draft, setDraft, defaults, catalog, special, focusKey, knownEvents, itemLabel, catalogNote, t }) {
    const [key, setKey] = useState(focusKey || ANY_KEY);
    const [query, setQuery] = useState('');
    const [newKey, setNewKey] = useState('');
    useEffect(() => { if (focusKey) setKey(focusKey); }, [focusKey]);

    const keys = useMemo(() => {
        const list = keysOf(draft, defaults);
        return key && !list.includes(key) ? [...list, key] : list;
    }, [draft, defaults, key]);
    const cur = itemsForKey(key, draft, defaults);
    const selected = cur.items;
    const diff = diffOf(key, selected, defaults);
    const known = useMemo(() => new Set([...(special || []), ...(catalog || []).map(c => c.name)]), [catalog, special]);
    const q = query.trim().toLowerCase();
    const match = (name, desc) => !q || name.toLowerCase().includes(q) || String(desc || '').toLowerCase().includes(q);
    const specialRows = (special || []).filter(s => match(s, itemLabel(s)));
    // 고른 것·기본값에서 뺀 것을 위로 — 수백 개(V$PARAMETER) 사이에 흩어지면 무엇이 걸려 있는지 안 보인다
    const pinned = (name) => selected.includes(name) || diff.removed.includes(name);
    const rows = (catalog || []).filter(c => match(c.name, c.desc)).sort((a, b) => (pinned(b.name) ? 1 : 0) - (pinned(a.name) ? 1 : 0));
    // 고른(또는 기본값의) 항목인데 이 DB 목록에 없는 것 — 지울 수 있게 맨 위에
    const orphan = [...new Set([...selected, ...diff.removed])].filter(n => !known.has(n));

    const toggle = (name) => {
        const next = selected.includes(name) ? selected.filter(m => m !== name) : [...selected, name];
        setDraft(prev => withItems(prev, defaults, key, next));
    };
    const addKey = () => { const k = newKey.trim(); if (k) { setKey(k); setNewKey(''); } };
    const badge = (name) => {
        if (selected.includes(name) && diff.added.includes(name)) return <span className="pset-mark add">{t('monitoring:perf.cfg.added')}</span>;
        if (!selected.includes(name) && diff.removed.includes(name)) return <span className="pset-mark del">{t('monitoring:perf.cfg.removed')}</span>;
        return null;
    };
    const row = (name, label, sub, desc) => (
        <label key={name} className={`pmm-row${selected.includes(name) ? ' on' : ''}`} title={desc || undefined}>
            <input type="checkbox" checked={selected.includes(name)} onChange={() => toggle(name)} />
            <span className="pmm-name">{label}</span>
            {sub ? <span className="pmm-unit">{sub}</span> : null}
            {badge(name)}
            <span className="pmm-desc">{desc || ''}</span>
        </label>
    );

    return (
        <div className="pmm-body pset-editor">
            <div className="pmm-keys">
                <div className="pmm-keysHead">{t('monitoring:perf.map.keys')}</div>
                <div className="pmm-keyList">
                    {keys.map((k) => (
                        <button type="button" key={k} className={`pmm-key${k === key ? ' on' : ''}`} onClick={() => setKey(k)} title={keyLabel(k, t)}>
                            <span className="pmm-keyName">{keyLabel(k, t)}</span>
                            {Array.isArray(draft[k]) ? <span className="pmm-badge" title={t('monitoring:perf.map.editedHint')}>{t('monitoring:perf.map.edited')}</span> : null}
                        </button>
                    ))}
                </div>
                <div className="pmm-addKey">
                    <input
                        className="pmm-input" list={`pset-events-${kind}`} value={newKey} placeholder={t('monitoring:perf.map.addKeyPh')}
                        aria-label={t('monitoring:perf.map.addKeyPh')}
                        onChange={(e) => setNewKey(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKey(); } }}
                    />
                    <datalist id={`pset-events-${kind}`}>
                        {(knownEvents || []).filter(e => !keys.includes(e)).map(e => <option key={e} value={e} />)}
                    </datalist>
                    <button type="button" className="app-btn" onClick={addKey} disabled={!newKey.trim()} title={t('monitoring:perf.map.addKey')}>
                        <FiPlus size={12} />
                    </button>
                </div>
            </div>

            <div className="pmm-metrics">
                <div className="pmm-metricsHead">
                    <span className="pmm-curKey">{keyLabel(key, t)}</span>
                    <span className="pmm-note">
                        {t(`monitoring:perf.map.source.${cur.source}`)} · {t('monitoring:perf.map.count', { count: selected.length })}
                        {(diff.added.length || diff.removed.length) && cur.source === 'user'
                            ? ` · ${t('monitoring:perf.cfg.diff', { added: diff.added.length, removed: diff.removed.length })}` : ''}
                    </span>
                    {cur.source === 'user' ? (
                        <button type="button" className="app-btn" onClick={() => setDraft(prev => withoutKey(prev, key))}
                            title={defaults[key] ? t('monitoring:perf.map.resetHint') : t('monitoring:perf.map.removeHint')}>
                            <FiRotateCcw size={11} /> {defaults[key] ? t('monitoring:perf.map.reset') : t('monitoring:perf.map.remove')}
                        </button>
                    ) : null}
                </div>
                <label className="pmm-search">
                    <FiSearch size={12} />
                    <input className="pmm-input" value={query} placeholder={t(kind === 'param' ? 'monitoring:perf.cfg.searchParam' : 'monitoring:perf.map.searchPh')} aria-label={t(kind === 'param' ? 'monitoring:perf.cfg.searchParam' : 'monitoring:perf.map.searchPh')} onChange={(e) => setQuery(e.target.value)} />
                </label>
                {catalogNote ? <div className="pmm-note">{catalogNote}</div> : null}
                <div className="pmm-list">
                    {orphan.map(name => (
                        <label key={`x-${name}`} className={`pmm-row missing${selected.includes(name) ? ' on' : ''}`} title={t('monitoring:perf.map.missingHint')}>
                            <input type="checkbox" checked={selected.includes(name)} onChange={() => toggle(name)} />
                            <span className="pmm-name">{itemLabel(name)}</span>
                            <span className="pmm-unit">{t('monitoring:perf.map.missing')}</span>
                            {badge(name)}
                        </label>
                    ))}
                    {specialRows.map(s => row(s, itemLabel(s), t('monitoring:perf.cfg.special'), null))}
                    {rows.map(c => row(c.name, c.name, c.unit, c.desc))}
                    {!rows.length && !specialRows.length && !orphan.length ? <div className="pmm-empty">{t('monitoring:perf.map.noMatch')}</div> : null}
                </div>
            </div>
        </div>
    );
}
MapEditor.propTypes = {
    kind: PropTypes.string.isRequired, draft: PropTypes.object.isRequired, setDraft: PropTypes.func.isRequired,
    defaults: PropTypes.object.isRequired, catalog: PropTypes.array, special: PropTypes.array, focusKey: PropTypes.string,
    knownEvents: PropTypes.array, itemLabel: PropTypes.func.isRequired, catalogNote: PropTypes.string, t: PropTypes.func.isRequired,
};

/** 공개 설정 하나의 고친 키 목록(읽기 전용) */
function SharedView({ data, paramDefaults, itemLabel, t }) {
    const section = (title, map, defaults, label) => {
        const keys = Object.keys(map || {}).filter(k => !k.startsWith('@'));
        return (
            <div className="pset-view-sec">
                <div className="pset-view-title">{title} — {t('monitoring:perf.cfg.changedKeys', { count: keys.length })}</div>
                {keys.length ? keys.map(k => {
                    const d = diffOf(k, map[k], defaults);
                    return (
                        <div key={k} className="pset-view-row">
                            <span className="pset-view-key">{keyLabel(k, t)}</span>
                            <span className="pset-view-items">
                                {map[k].length ? map[k].map(i => <span key={i} className={`pset-chip${d.added.includes(i) ? ' add' : ''}`}>{label(i)}</span>) : <span className="pmm-note">{t('monitoring:perf.cfg.none')}</span>}
                                {d.removed.map(i => <span key={`-${i}`} className="pset-chip del">{label(i)}</span>)}
                            </span>
                        </div>
                    );
                }) : <div className="pmm-note">{t('monitoring:perf.cfg.noChanges')}</div>}
            </div>
        );
    };
    return (
        <div className="pset-view">
            {section(t('monitoring:perf.cfg.sec.metric'), data.map, DEFAULT_MAP, (x) => x)}
            {section(t('monitoring:perf.cfg.sec.param'), data.paramMap, paramDefaults, itemLabel)}
        </div>
    );
}
SharedView.propTypes = { data: PropTypes.object.isRequired, paramDefaults: PropTypes.object.isRequired, itemLabel: PropTypes.func.isRequired, t: PropTypes.func.isRequired };

function PerfSettingsPanel({ api, target, run, metricCatalog, metricDesc, knownEvents, focus, onSaved }) {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const [section, setSection] = useState((focus && focus.section) || 'metric');
    useEffect(() => { if (focus && focus.section) setSection(focus.section); }, [focus]);
    const [saved, setSaved] = useState({ map: {}, paramMap: {}, shared: false, sharedAt: null });
    const [metricDraft, setMetricDraft] = useState({});
    const [paramDraft, setParamDraft] = useState({});
    const [paramDefaults, setParamDefaults] = useState({});
    const [special, setSpecial] = useState([]);
    const [paramCatalog, setParamCatalog] = useState([]);
    const [paramCatalogNote, setParamCatalogNote] = useState(null);
    const [busy, setBusy] = useState(false);
    const [sharedList, setSharedList] = useState([]);
    const [viewing, setViewing] = useState(null);       // { id, data }

    const load = useCallback(async () => {
        try {
            const [mine, defs] = await Promise.all([axios.get(`${api}/metric-map`), axios.get(`${api}/param-defaults`)]);
            const m = mine.data || {};
            setSaved({ map: m.map || {}, paramMap: m.paramMap || {}, shared: !!m.shared, sharedAt: m.sharedAt || null });
            setMetricDraft(m.map || {}); setParamDraft(m.paramMap || {});
            setParamDefaults((defs.data && defs.data.defaults) || {});
            setSpecial((defs.data && defs.data.special) || []);
        } catch (err) { toast.error(formatApiError(err, t)); }
    }, [api, t]);
    useEffect(() => { load(); }, [load]);
    // 고를 수 있는 파라미터 — 이 DB 의 V$PARAMETER(못 읽으면 기본값의 이름만으로 고친다)
    useEffect(() => {
        let alive = true;
        run(() => axios.get(`${api}/param-catalog`, { params: target({}) }))
            .then(r => { if (alive) { setParamCatalog((r.data && r.data.data) || []); setParamCatalogNote(r.data && r.data.isDemo ? t('monitoring:perf.cfg.catalogDemo') : null); } })
            .catch(() => { if (alive) setParamCatalogNote(t('monitoring:perf.cfg.catalogFailed')); });
        return () => { alive = false; };
    }, [api, run, target, t]);
    const loadShared = useCallback(() => {
        axios.get(`${api}/metric-map/shared`).then(r => setSharedList((r.data && r.data.list) || [])).catch(() => setSharedList([]));
    }, [api]);
    useEffect(() => { if (section === 'share') loadShared(); }, [section, loadShared]);

    const itemLabel = useCallback((id) => (/^(db|redo):/.test(id) ? t(`monitoring:perf.set.item.${id.split(':')[1]}`) : id), [t]);
    const dirty = JSON.stringify(metricDraft) !== JSON.stringify(saved.map) || JSON.stringify(paramDraft) !== JSON.stringify(saved.paramMap);

    const save = async () => {
        setBusy(true);
        try {
            const r = await axios.put(`${api}/metric-map`, { map: metricDraft, paramMap: paramDraft });
            const m = r.data || {};
            setSaved(s => ({ ...s, map: m.map || {}, paramMap: m.paramMap || {} }));
            setMetricDraft(m.map || {}); setParamDraft(m.paramMap || {});
            if (onSaved) onSaved(m);
            toast.success(t('monitoring:perf.cfg.saved'));
        } catch (err) { toast.error(formatApiError(err, t)); }
        finally { setBusy(false); }
    };
    // 되돌리기 = 마지막으로 저장한 상태로(두 매핑 모두). 기본값으로 = 지금 갈래의 고친 것을 모두 지운다 — 저장해야 반영된다
    const discard = () => { setMetricDraft(saved.map); setParamDraft(saved.paramMap); };
    const toDefaults = () => { if (section === 'metric') setMetricDraft({}); else if (section === 'param') setParamDraft({}); };
    const sectionDraft = section === 'metric' ? metricDraft : section === 'param' ? paramDraft : null;
    const toggleShare = async (on) => {
        setBusy(true);
        try {
            const r = await axios.put(`${api}/metric-map/shared`, { on });
            setSaved(s => ({ ...s, shared: !!(r.data && r.data.shared), sharedAt: (r.data && r.data.sharedAt) || null }));
            toast.success(t(on ? 'monitoring:perf.cfg.sharedOn' : 'monitoring:perf.cfg.sharedOff'));
        } catch (err) { toast.error(formatApiError(err, t)); }
        finally { setBusy(false); }
    };
    const view = async (id) => {
        if (viewing && viewing.id === id) { setViewing(null); return; }
        try { const r = await axios.get(`${api}/metric-map/shared/${encodeURIComponent(id)}`); setViewing({ id, data: r.data }); }
        catch (err) { toast.error(formatApiError(err, t)); }
    };
    const copy = async (s) => {
        const ok = await confirm({
            title: t('monitoring:perf.cfg.copyTitle'),
            message: t('monitoring:perf.cfg.copyConfirm', { by: s.by || t('monitoring:perf.cfg.someone') }),
            confirmText: t('monitoring:perf.cfg.copy'), variant: 'danger',
        });
        if (!ok) return;
        setBusy(true);
        try {
            const r = await axios.post(`${api}/metric-map/shared/${encodeURIComponent(s.id)}/copy`, {});
            const m = r.data || {};
            setSaved(prev => ({ ...prev, map: m.map || {}, paramMap: m.paramMap || {} }));
            setMetricDraft(m.map || {}); setParamDraft(m.paramMap || {});
            if (onSaved) onSaved(m);
            toast.success(t('monitoring:perf.cfg.copied'));
        } catch (err) { toast.error(formatApiError(err, t)); }
        finally { setBusy(false); }
    };

    const metricCat = useMemo(() => (metricCatalog || []).map(m => ({ name: m.metricName, unit: m.metricUnit, desc: (metricDesc && metricDesc[m.metricName]) || '' })), [metricCatalog, metricDesc]);
    const paramCat = useMemo(() => paramCatalog.map(p => ({ name: p.name, unit: null, desc: p.description || '' })), [paramCatalog]);
    const focusKey = focus && focus.key ? focus.key : undefined;

    return (
        <div className="pset">
            <div className="pset-top">
                <span className="dbmsperf-seg" role="tablist" aria-label={t('monitoring:perf.cfg.title')}>
                    {['metric', 'param', 'share'].map(s => (
                        <button key={s} type="button" role="tab" aria-selected={section === s}
                            className={`dbmsperf-seg-btn${section === s ? ' on' : ''}`} onClick={() => setSection(s)}>
                            {t(`monitoring:perf.cfg.sec.${s}`)}
                            {s === 'metric' && changedCount(metricDraft) ? <span className="pset-count">{changedCount(metricDraft)}</span> : null}
                            {s === 'param' && changedCount(paramDraft) ? <span className="pset-count">{changedCount(paramDraft)}</span> : null}
                        </button>
                    ))}
                </span>
                <span className="pmm-note pset-hint">{t(`monitoring:perf.cfg.hint.${section}`)}</span>
                <span className="pset-spacer" />
                {dirty ? <span className="pset-dirty">{t('monitoring:perf.cfg.dirty')}</span> : null}
                {sectionDraft ? (
                    <button type="button" className="app-btn" disabled={!Object.keys(sectionDraft).length || busy} onClick={toDefaults}
                        title={t(`monitoring:perf.cfg.toDefaultsHint.${section}`)}><FiRotateCcw size={12} /> {t('monitoring:perf.cfg.toDefaults')}</button>
                ) : null}
                <button type="button" className="app-btn" disabled={!dirty || busy} onClick={discard} title={t('monitoring:perf.cfg.discardHint')}>{t('monitoring:perf.cfg.discard')}</button>
                <button type="button" className="app-btn pset-save" disabled={!dirty || busy} onClick={save}><FiSave size={12} /> {t('monitoring:perf.map.save')}</button>
            </div>

            <div className="pset-scroll">
            {section === 'metric' && (
                <MapEditor kind="metric" draft={metricDraft} setDraft={setMetricDraft} defaults={DEFAULT_MAP} catalog={metricCat}
                    focusKey={focus && focus.section === 'metric' ? focusKey : undefined} knownEvents={knownEvents} itemLabel={(x) => x}
                    catalogNote={metricCat.length ? null : t('monitoring:perf.noCatalog')} t={t} />
            )}
            {section === 'param' && (
                <MapEditor kind="param" draft={paramDraft} setDraft={setParamDraft} defaults={paramDefaults} catalog={paramCat} special={special}
                    focusKey={focus && focus.section === 'param' ? focusKey : undefined} knownEvents={knownEvents} itemLabel={itemLabel}
                    catalogNote={paramCatalogNote} t={t} />
            )}
            {section === 'share' && (
                <div className="pset-share">
                    <div className="pset-mine">
                        <label className="pset-check">
                            <input type="checkbox" checked={saved.shared} disabled={busy} onChange={(e) => toggleShare(e.target.checked)} />
                            <FiShare2 size={12} /> {t('monitoring:perf.cfg.shareMine')}
                        </label>
                        <span className="pmm-note">
                            {t('monitoring:perf.cfg.mineSummary', { metric: changedCount(saved.map), param: changedCount(saved.paramMap) })}
                            {saved.shared && saved.sharedAt ? ` · ${t('monitoring:perf.cfg.sharedAt', { at: String(saved.sharedAt).slice(0, 16).replace('T', ' ') })}` : ''}
                        </span>
                        <div className="pmm-note">{t('monitoring:perf.cfg.shareNote')}</div>
                    </div>
                    <div className="pset-view-title">{t('monitoring:perf.cfg.othersTitle')}</div>
                    {sharedList.length ? sharedList.map(s => (
                        <div key={s.id} className="pset-shared">
                            <div className="pset-shared-row">
                                <span className="pset-shared-by">{s.by || t('monitoring:perf.cfg.someone')}</span>
                                <span className="pmm-note">
                                    {t('monitoring:perf.cfg.sharedSummary', { metric: s.metricKeys, param: s.paramKeys, at: storedTime(s.updatedAt || s.sharedAt, 'min') })}
                                </span>
                                <span className="pset-spacer" />
                                <button type="button" className="app-btn" onClick={() => view(s.id)}><FiEye size={12} /> {t('monitoring:perf.cfg.view')}</button>
                                <button type="button" className="app-btn" disabled={busy} onClick={() => copy(s)}><FiCopy size={12} /> {t('monitoring:perf.cfg.copy')}</button>
                            </div>
                            {viewing && viewing.id === s.id ? <SharedView data={viewing.data} paramDefaults={paramDefaults} itemLabel={itemLabel} t={t} /> : null}
                        </div>
                    )) : <div className="pmm-note">{t('monitoring:perf.cfg.noShared')}</div>}
                </div>
            )}
            </div>
        </div>
    );
}

PerfSettingsPanel.propTypes = {
    api: PropTypes.string.isRequired,
    target: PropTypes.func.isRequired,
    run: PropTypes.func.isRequired,
    metricCatalog: PropTypes.array,
    metricDesc: PropTypes.object,
    knownEvents: PropTypes.array,
    /** 「매핑 편집」 에서 넘어올 때 — { section: 'metric'|'param', key, nonce } */
    focus: PropTypes.object,
    /** 저장·복제 뒤 — 실시간 성능·성능 이력이 새 매핑으로 그리게 */
    onSaved: PropTypes.func,
};

export default PerfSettingsPanel;
